import { useEffect } from "react";

/**
 * One IntersectionObserver that toggles `is-visible` on `.reveal` elements.
 * - Idempotent (each element revealed exactly once)
 * - Self-cleans MutationObserver after the page stops mutating
 * - Respects prefers-reduced-motion (shows everything immediately)
 */
export default function useScrollReveal() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

    const showAll = () =>
      document
        .querySelectorAll(".reveal")
        .forEach((el) => el.classList.add("is-visible"));

    if (reduce || !("IntersectionObserver" in window)) {
      showAll();
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "-40px 0px", threshold: 0.08 }
    );

    const observe = () =>
      document.querySelectorAll(".reveal:not(.is-visible)").forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.bottom < 0) {
          el.classList.add("is-visible");
          return;
        }
        io.observe(el);
      });

    observe();

    // Re-scan briefly while React mounts late children, then disconnect.
    const mo = new MutationObserver(() => observe());
    mo.observe(document.body, { childList: true, subtree: true });

    // Disconnect MO after the page is stable to save CPU. IO keeps running.
    const moTimeout = window.setTimeout(() => mo.disconnect(), 3000);

    return () => {
      window.clearTimeout(moTimeout);
      io.disconnect();
      mo.disconnect();
    };
  }, []);
}
